<template>
	<link-input
		ref="input"
		:value="model.value"
		class="link-input-number"
		:placeholder="placeholder"
		:replacement="replacement"
		:inputType="inputType"
		:regexp="regexp"
		:width="width"
		:size="size"
		:type="type"
		:color="color"
		:long="long"
		:disabled="lv_disabled"
		:shape="shape"
		:dashed="dashed"
		:readonly="lv_readonly"
		:focus-on-hover="focusOnHover"
		keyboard
		suffixIcon=""
		:hide-clear-icon="hideClearIcon"
		:align="align"

		:required="required"
		:validate-on-init="validateOnInit"
		:rules="rules"

		@click-suffix-icon="_clear"
		@mouseleave.native="$emit('leave', value)"
		@input="_input"
		@keydown="_keydown"
		@focus="_focus"
		@blur="_blur">
		<template slot="append" v-if="showIcon">
			<div class="link-input-number-button-all">
				<div class="link-input-number-button" @mousedown.stop="intervalAdd">
					<link-icon icon="link-icon-up" class="link-input-number-addition-icon"/>
				</div>
				<div class="link-input-number-button" @mousedown.stop="intervalMinus">
					<link-icon icon="link-icon-down" class="link-input-number-subtraction-icon"/>
				</div>
			</div>
		</template>
	</link-input>
</template>

<script>

	import {EditMixin} from '../mixins';
	import Keyboard from "../../utils/keyboard";

	const NAN = 'NAN'

	/**
	 * 数字输入框
	 * @author  韦胜健
	 * @date    2020/12/3 18:08
	 */
	export default {
		name: "link-input-number",
		mixins: [EditMixin],
		props: {
			value: {type: [Number, String], default: null},             // 组件绑定的数据
			max: {type: Number},                                        // 设置计数器最大值
			min: {type: Number},                                        // 设置计数器最小值
			space: {type: Number, default: 1},                          // 设置计数器每次增加的数量
			showIcon: {type: Boolean, default: true},                   // 设置计数器上下icon的显示与隐藏

			stepStrictly: {type: Boolean},                              // 是否只能输入 space 的倍数
			precision: {type: [Number, String]},                        // 数值精度
			isInteger: {type: Boolean},									//

			regexp: {type: RegExp, default: () => /[^\-0-9.]/g},        // 设置input框的校验
			placeholder: {type: String, default: ''},                   // 设置input框的提示语
			replacement: {type: String, default: ''},                   // 设置替换文本或生成替换文本的函数。
			inputType: {type: String, default: 'text'},                 // 设置input框的类型格式
			type: {type: String, default: 'line'},                      // 设置input框的类型样式
			color: {type: String, default: 'info'},                     // 设置input框的颜色
			size: {type: String, default: 'default'},                   // 设置input框的大小
			long: {type: Boolean},                                      // 设置input框的长度为100%
			hideClearIcon: {type: Boolean},                             // 是否禁用清除图标
			width: {type: Number, default: 88},                        // 设置input框的长度
			dashed: {type: Boolean},                                    // 设置input框的边框（无边框/虚线边框）
			shape: {type: String, default: 'none'},                     // 设置input框的形状
			focusOnHover: {},
			align: {type: String},

			required: {type: Boolean},
			rules: {type: Array | String},
			validateOnInit: {type: Boolean,},
		},
		data() {

			/*双向绑定队形*/
			const model = {
				p_value: this.value,
				get value() {return this.p_value},
				set value(val) {
					this.p_value = val === "" ? null : val
				},
			}
			this.$watch(() => this.value, val => {
				if (model.value != val) {
					model.p_value = val
				}
			})

			return {
				model,
				/*进步器长摁定时器*/
				interval: null,
				updateModelValue: (val) => {
					this.$emit('input', val)
					this.$emit('change', val)
				},
				clearInterval: () => {
					if (this.interval != null) {
						clearInterval(this.interval)
					}
					window.removeEventListener('mouseup', this.clearInterval)
				},
			}
		},
		computed: {
			p_precision() {
				return this.isInteger ? 0 : this.precision
			},
			/**
			 * 数字格式化后的modelValue
			 * @author  韦胜健
			 * @date    2020/12/3 18:13
			 */
			formatModelValue() {
				return this.getValue(this.model.value)
			},
			/**
			 * 数字格式化之后的props.value
			 * @author  韦胜健
			 * @date    2020/12/3 18:13
			 */
			formatValue() {
				return this.getValue(this.value)
			},
		},
		methods: {

			/*---------------------------------------utils-------------------------------------------*/
			/**
			 * 获取数字
			 * @author  韦胜健
			 * @date    2020/12/3 18:11
			 */
			getValue(val) {
				if (val == null) return undefined
				val = String(val).trim()
				if (val === '') return undefined
				val = Number(val)
				if (isNaN(val)) {
					return NAN
				} else {
					return val
				}
			},
			/**
			 * 获取有效数字，最大最小值
			 * @author  韦胜健
			 * @date    2020/12/3 18:12
			 */
			getEffectiveValue() {
				let value = this.formatModelValue
				if (value === NAN) {
					value = this.formatValue
					if (value === NAN) {
						if (this.min != null) return Number(this.min)
						else if (this.max != null) return Number(this.max)
						else if (this.stepStrictly) return Number(this.space)
						else return 0
					}
				}
				return value
			},
			/**
			 * 检查值是否合法
			 * @author  韦胜健
			 * @date    2020/12/3 18:15
			 */
			checkValue(value) {
				if (value != null) {
					// min
					if (this.min != null && value < this.min) {
						value = this.min
					}
					// max
					if (this.max != null && value > this.max) {
						value = this.max
					}
					// stepStrictly
					if (this.stepStrictly && value % this.space !== 0) {
						// console.log('props.value', props.modelValue)
						value = this.value == null ? undefined : Number(this.value)
					}
					// precision
					if (this.p_precision != null) {
						value = Number(value.toFixed(this.p_precision))
					}
				}
				return value
			},

			/*---------------------------------------handler-------------------------------------------*/

			/**
			 * @method：_additive
			 * @description：计数器数量增加函数
			 */
			_additive() {
				if (this.lv_disabled || this.lv_readonly) return

				let value = this.getEffectiveValue()
				if (value == null) {
					value = 0
				}
				value += Number(this.space)
				value = this.checkValue(value)
				this.model.value = value
				this.updateModelValue(this.model.value)
			},
			/**
			 * @method：_subtraction
			 * @description：计数器数量减少函数
			 */
			_subtraction() {
				if (this.lv_disabled || this.lv_readonly) return
				let value = this.getEffectiveValue()
				if (value == null) {
					value = 0
				}
				value -= this.space
				value = this.checkValue(value)
				this.model.value = value
				this.updateModelValue(this.model.value)
			},

			_clear() {
				if (this.lv_disabled || this.lv_readonly) return
				this.model.value = null
				this.updateModelValue(this.model.value)
			},
			_focus(e) {
				this.$emit('focus', e)
			},
			_blur(e) {
				this.model.value = this.checkValue(this.getEffectiveValue())
				this.updateModelValue(this.model.value)
				this.$emit('blur', e)
			},
			_input(val) {
				this.model.value = val
			},
			_enter(e) {
				this.model.value = this.checkValue(this.getEffectiveValue())
				this.updateModelValue(this.model.value)
				this.$emit('enter', e)
			},
			_keydown(e) {
				const key = Keyboard.code[e.keyCode]
				switch (key) {
					case 'up':
						e.preventDefault()
						e.stopPropagation()
						this._additive()
						break
					case 'down':
						e.preventDefault()
						e.stopPropagation()
						this._subtraction()
						break
					case 'enter':
						this._enter(e)
						break
				}
			},

			intervalAdd() {
				this._additive()
				this.interval = setInterval(this._additive, 100)
				window.addEventListener('mouseup', this.clearInterval)
			},
			intervalMinus() {
				this._subtraction()
				this.interval = setInterval(this._subtraction, 100)
				window.addEventListener('mouseup', this.clearInterval)
			},
		}
	}
</script>

<style lang="scss">
	@include themeify {
		.link-input-number {
			box-sizing: border-box;

			.link-input-clear-icon {
				padding-right: 2px !important;
				right: 4px;
			}

			.link-input-append {
				height: 100% !important;
				line-height: normal !important;
			}

			.link-input-number-button-all {
				height: 100%;
				line-height: 0;
				display: block;
				align-items: baseline;
				color: $icon-color;

				.link-input-number-button {
					height: 50%;
					display: flex;
					align-items: center;
					cursor: pointer;
					user-select: none;
					border-left: solid 1px pl-var(color-info);
					padding: 0 3px;
					font-size: 12px;
					position: relative;

					&:active {
						background-color: #ddd;
					}

					.link-icon {
						transform: scale(0.7);
					}

					&:first-child:after {
						content: '';
						position: absolute;
						height: 1px;
						left: 0;
						right: 0;
						bottom: -0.5px;
						background-color: pl-var(color-info);
					}
				}
			}
		}

	}
</style>

